// src/components/team/MemberVisibilitySection.tsx
// Member visibility assignment section for selected-record visibility
//
// Shows assigned records when a member has 'selected' visibility for an entity.
// Allows admins to view, add, and remove assigned records.

import { useState } from 'react';
import Button from '../ui/Button';
import {
  useGetMemberVisibilityQuery,
  useUpdateMemberVisibilityMutation,
  SUPPORTED_ENTITIES,
  extractEntityVisibility,
  type EntityConfig,
  type DomainVisibility,
} from '../../api/memberVisibilityApi';
import VisibilityAssignmentModal from './VisibilityAssignmentModal';

// ============================================================
// Types
// ============================================================

interface MemberVisibilitySectionProps {
  /** User ID of the member */
  memberId: string;
  /** Workspace ID */
  workspaceId: string;
  /** Whether the current user can manage visibility (admin/owner) */
  canManage: boolean;
}

interface AssignmentChipProps {
  id: string;
  name: string;
  onRemove?: () => void;
  isRemoving?: boolean;
}

// ============================================================
// AssignmentChip Component
// ============================================================

function AssignmentChip({ name, onRemove, isRemoving }: AssignmentChipProps) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
      <span className="truncate max-w-[150px]" title={name}>
        {name}
      </span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={isRemoving}
          className="hover:bg-blue-100 rounded-full p-0.5 transition-colors disabled:opacity-50"
          aria-label={`Remove ${name}`}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </span>
  );
}

// ============================================================
// EntityAssignmentSection Component
// ============================================================

interface EntityAssignmentSectionProps {
  config: EntityConfig;
  visibility: DomainVisibility | undefined;
  workspaceId: string;
  membershipId: string;
  canManage: boolean;
}

function EntityAssignmentSection({
  config,
  visibility,
  workspaceId,
  membershipId,
  canManage,
}: EntityAssignmentSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updateVisibility, { isLoading: isUpdating }] = useUpdateMemberVisibilityMutation();
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Only show if visibility is 'selected'
  if (visibility?.level !== 'selected') {
    return null;
  }

  const assignedIds = visibility.ids || [];
  const records = visibility.records || [];

  // Build display list - use records if available, else just IDs
  const displayItems = assignedIds.map((id) => {
    const record = records.find((r) => r.id === id);
    return { id, name: record?.name || id };
  });

  const handleRemove = async (id: string) => {
    setRemovingId(id);
    try {
      await updateVisibility({
        workspaceId,
        membershipId,
        app: config.app,
        entity: config.entity,
        remove: [id],
      }).unwrap();
    } catch (error) {
      console.error('Failed to remove assignment:', error);
    } finally {
      setRemovingId(null);
    }
  };

  const handleAssign = async (ids: string[]) => {
    try {
      await updateVisibility({
        workspaceId,
        membershipId,
        app: config.app,
        entity: config.entity,
        add: ids,
      }).unwrap();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to add assignments:', error);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-medium text-gray-700">{config.pluralLabel}</h5>
        {canManage && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsModalOpen(true)}
            className="text-blue-600 hover:text-blue-700"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add
          </Button>
        )}
      </div>

      {displayItems.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No {config.pluralLabel.toLowerCase()} assigned</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {displayItems.map((item) => (
            <AssignmentChip
              key={item.id}
              id={item.id}
              name={item.name}
              onRemove={canManage ? () => handleRemove(item.id) : undefined}
              isRemoving={removingId === item.id || isUpdating}
            />
          ))}
        </div>
      )}

      {/* Assignment Modal */}
      <VisibilityAssignmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAssign={handleAssign}
        config={config}
        workspaceId={workspaceId}
        existingIds={assignedIds}
      />
    </div>
  );
}

// ============================================================
// MemberVisibilitySection Component
// ============================================================

export default function MemberVisibilitySection({
  memberId,
  workspaceId,
  canManage,
}: MemberVisibilitySectionProps) {
  const { data: visibilityData, isLoading, error } = useGetMemberVisibilityQuery(
    { workspaceId, membershipId: memberId },
    { skip: !workspaceId || !memberId }
  );

  // Show loading state
  if (isLoading) {
    return (
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Data Access</h4>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-8 bg-gray-100 rounded w-full" />
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Data Access</h4>
        <p className="text-sm text-red-600">Failed to load visibility settings</p>
      </div>
    );
  }

  // Check if any entity has 'selected' visibility
  const hasAnySelectedVisibility = SUPPORTED_ENTITIES.some((config) => {
    const visibility = extractEntityVisibility(visibilityData, config.app, config.entity);
    return visibility?.level === 'selected';
  });

  // Don't render section if no entities have selected visibility
  if (!hasAnySelectedVisibility) {
    return null;
  }

  return (
    <div className="pt-4 border-t border-gray-200">
      <h4 className="text-sm font-medium text-gray-700 mb-3">Data Access</h4>
      <div className="space-y-4">
        {SUPPORTED_ENTITIES.map((config) => (
          <EntityAssignmentSection
            key={`${config.app}-${config.entity}`}
            config={config}
            visibility={extractEntityVisibility(visibilityData, config.app, config.entity)}
            workspaceId={workspaceId}
            membershipId={memberId}
            canManage={canManage}
          />
        ))}
      </div>
    </div>
  );
}
